<template>
  <div
    :class="[
      'PromoPlayer bg-white/90 bg-blur dark:bg-primary-darkest/90 shadow-top duration-300 select-none',
      activeAudioIndex !== null ? 'translate-y-0' : 'translate-y-full',
      isIOS ? 'pb-gr-4' : '',
    ]"
  >
    <div
      @click="onSeek"
      class="h-gr-6 cursor-pointer duration-200 relative bg-white dark:bg-primary-darkest"
    >
      <div
        class="pointer-events-none absolute left-0 bottom-0 top-gr-half-6 w-full bg-gray-50 dark:bg-gray-900/50"
      ></div>
      <div
        class="pointer-events-none absolute left-0 bottom-0 top-gr-half-6 duration-250 bg-gray-200/75 dark:bg-gray-800/75"
        :style="{ width: getTrackProgressWidth(activeAudioIndex) }"
      ></div>
      <div
        :class="[
          'pointer-events-none absolute left-0 bottom-0 top-gr-half-6 duration-100 bg-tertiary-400 dark:bg-tertiary-500',
          { 'ease-linear': isTrackPlaying(activeAudioIndex) },
          { 'bg-diag-bars': !isSafari && !isIOS && !isTrackReady(activeAudioIndex) },
        ]"
        :style="{ width: getTrackPositionWidth(activeAudioIndex) }"
      ></div>
      <input
        v-if="activeAudioIndex !== null"
        type="range"
        class="absolute h-gr-half-6 w-full bottom-0 duration-250"
        v-model="range"
        @input="onRangeInput"
        min="0"
        max="100"
        step="0.01"
      />
    </div>
    <div
      v-if="activeTrack"
      class="opacity-50 container max-w-4xl text-center leading-gr-half-7 pt-gr-4 text-sm flex items-center justify-between"
    >
      <span class="px-gr-half-4">
        {{ timeRemaining }}
      </span>
      <span class="truncate flex-grow"> {{ getTrackName(activeAudioIndex) }} </span>
      <span class="px-gr-half-4">
        {{ timeTotal }}
      </span>
    </div>
    <div
      class="pt-gr-4 pb-gr-half-6 h-gr-double-6 box-content text-primary-700 dark:text-primary-400 flex justify-center items-center text-lg xs:text-2xl relative space-x-gr-2 xs:space-x-gr-5 sm:space-x-gr-6"
    >
      <button @click="onPrevioustrack" class="p-0 button w-gr-7 h-gr-7 rounded">
        <i class="fal fa-fw fa-arrow-to-left"></i>
      </button>
      <div class="relative">
        <button @click="onSeekbackward" class="p-0 button w-gr-7 h-gr-7 rounded rotate-4">
          <i class="fal fa-fw fa-undo"></i>
        </button>
        <span
          class="absolute text-[12px] font-semibold tracking-tighter -left-gr-1 xs:-left-gr-2 bottom-gr-1"
        >
          30
        </span>
      </div>
      <button
        ref="playPauseButton"
        @click="onPlayPause"
        :class="[
          'p-0 button w-gr-7 h-gr-7 rounded-full',
          { 'pointer-events-none': !isIOS && isTrackLoading(activeAudioIndex) },
        ]"
      >
        <span :class="{ hidden: isIOS || !isTrackLoading(activeAudioIndex) }">
          <i class="fad fa-2x fa-fw fa-spinner-third fa-spin"></i>
        </span>
        <span
          :class="{
            hidden:
              (!isIOS && isTrackLoading(activeAudioIndex)) || isTrackPlaying(activeAudioIndex),
          }"
        >
          <i class="fad fa-2x fa-fw fa-play-circle"></i>
        </span>
        <span
          :class="{
            hidden:
              (!isIOS && isTrackLoading(activeAudioIndex)) || !isTrackPlaying(activeAudioIndex),
          }"
        >
          <i class="fad fa-2x fa-fw fa-pause-circle"></i>
        </span>
      </button>
      <div class="relative">
        <button @click="onSeekforward" class="p-0 button w-gr-7 h-gr-7 rounded rotate-">
          <i class="fal fa-fw fa-redo"></i>
        </button>
        <span
          class="absolute text-[12px] font-semibold tracking-tighter -right-gr-1 xs:-right-gr-2 bottom-gr-1"
        >
          30
        </span>
      </div>
      <button @click="onNexttrack" class="p-0 button w-gr-7 h-gr-7 rounded">
        <i class="fal fa-fw fa-arrow-to-right"></i>
      </button>
      <button
        @click="onPlaybackRate"
        class="py-0 px-gr-half-4 box-content button w-gr-7 h-gr-7 absolute top-1/2 -translate-y-1/2 translate-x-gr-half-4 right-gr-5 sm:right-gr-6 lg:right-gr-7 font-normal text-lg xs:text-2xl"
      >
        {{ playbackRate }}x
      </button>
    </div>
  </div>
</template>

<script>
  import { AudioBus } from '@/Utils/bus';
  import { isIOS, isSafari } from '@/Utils/device';
  import { convertTimeHHMMSS } from '@/Utils/date';

  export default {
    name: 'PromoPlayer',

    props: ['tracks', 'book'],

    data() {
      return {
        audioPlaying: null,
        audioProgress: {},
        audioLoading: {},
        audioPosition: {},
        audioCanPlay: {},
        activeAudio: null,
        activeAudioIndex: null,
        timeRemaining: '--:--',
        timeTotal: '--:--',
        playbackRate: 1,
        range: 0,
        rangeInputTimeout: 0,
        isIOS,
        isSafari,
      };
    },

    computed: {
      percentComplete() {
        return this.activeAudioIndex !== null ? this.audioPosition[this.activeAudioIndex] : 0;
      },

      activeTrack() {
        return this.tracks[this.activeAudioIndex];
      },
    },

    watch: {
      percentComplete(val) {
        this.range = val || 0;
      },

      playbackRate(val) {
        this.onTimeUpdate({
          index: this.activeAudioIndex,
          audio: this.activeAudio,
          event: 'playbackRate',
        });
      },

      activeAudioIndex(val, oldVal) {
        if (val !== null && val !== oldVal) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.$refs['playPauseButton']?.focus();
            }, 100);
          });
        }
      },
    },

    mounted() {
      this.range = this.percentComplete || 0;
      AudioBus.$on('activeAudioIndex', (val) => (this.activeAudioIndex = val));
      AudioBus.$on('audioCanPlay', (val) => (this.audioCanPlay = val));
      AudioBus.$on('audioPlaying', (val) => (this.audioPlaying = val));
      AudioBus.$on('audioLoading', (val) => (this.audioLoading = val));
      AudioBus.$on('audioProgress', (val) => (this.audioProgress = val));
      AudioBus.$on('audioPosition', (val) => (this.audioPosition = val));
      AudioBus.$on('playbackRate', (val) => (this.playbackRate = parseFloat(val)));
      AudioBus.$on('timeupdate', this.onTimeUpdate);
    },

    beforeUnmount() {
      AudioBus.$off('activeAudioIndex');
      AudioBus.$off('audioCanPlay');
      AudioBus.$off('audioPlaying');
      AudioBus.$off('audioLoading');
      AudioBus.$off('audioProgress');
      AudioBus.$off('audioPosition');
      AudioBus.$off('playbackRate');
      AudioBus.$off('timeupdate');
    },

    methods: {
      onTimeUpdate({ index, audio, event }) {
        if (audio?.duration) {
          this.activeAudio = audio;
          let timeRemaining = (audio.duration - audio.currentTime) / this.playbackRate;
          this.timeRemaining = convertTimeHHMMSS(timeRemaining);
          this.timeTotal = convertTimeHHMMSS(audio.duration / this.playbackRate);
        }
      },

      onRangeInput(event) {
        clearTimeout(this.rangeInputTimeout);
        this.rangeInputTimeout = setTimeout(() => {
          this.onSeek(event);
        }, 50);
      },

      isTrackLoading(index) {
        return this.audioLoading[index];
      },

      isTrackReady(index) {
        return this.audioCanPlay[index];
      },

      isTrackPlaying(index) {
        return this.audioPlaying === index;
      },

      getTrackProgressWidth(index) {
        return this.audioProgress[index] + '%';
      },

      getTrackPositionWidth(index) {
        return this.audioPosition[index] + '%';
      },

      getTrackName(index) {
        return this.tracks[index]?.name;
      },

      onPlayPause() {
        if (this.audioPlaying !== null) {
          AudioBus.$emit('onPause');
        } else {
          AudioBus.$emit('onPlay', this.activeAudioIndex);
        }
      },

      onSeekbackward() {
        AudioBus.$emit('onSeekbackward');
      },

      onSeekforward() {
        AudioBus.$emit('onSeekforward');
      },

      onPrevioustrack() {
        AudioBus.$emit('onPrevioustrack');
      },

      onNexttrack() {
        AudioBus.$emit('onNexttrack');
      },

      onSeek(event) {
        AudioBus.$emit('onSeek', { event, index: this.activeAudioIndex });
      },

      onPlaybackRate() {
        AudioBus.$emit('onPlaybackRate');
      },
    },
  };
</script>

<!-- <style lang="scss" src="./PromoPlayer.scss" scoped></style> -->
<style lang="scss" scoped>
  input[type='range'] {
    -webkit-appearance: none; /* Hides the slider so that custom slider can be made */
    width: 100%; /* Specific width is required for Firefox. */
    background: transparent; /* Otherwise white in Chrome */
  }

  input[type='range']:focus {
    outline: none; /* Removes the blue border. You should probably do some kind of focus styling for accessibility reasons though. */
  }

  input[type='range']::-ms-track {
    width: 100%;
    cursor: pointer;

    /* Hides the slider so custom styles can be added */
    background: transparent;
    border-color: transparent;
    color: transparent;
  }

  /* Special styling for WebKit/Blink */
  input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none;
    @apply border-none duration-200 w-gr-5 h-gr-5 -ml-px mt-gr-half-3 bg-white dark:bg-tertiary-600 ring-4 ring-tertiary-300 ring-opacity-100 shadow-lg rounded-full cursor-pointer -translate-y-1/2;
  }

  /* All the same stuff for Firefox */
  input[type='range']::-moz-range-thumb {
    @apply w-gr-5 h-gr-5 bg-tertiary-500 shadow-lg rounded-full cursor-pointer;
  }

  /* All the same stuff for IE */
  input[type='range']::-ms-thumb {
    @apply w-gr-5 h-gr-5 bg-tertiary-500 shadow-lg rounded-full cursor-pointer;
  }

  input[type='range']::-webkit-slider-runnable-track {
    @apply h-gr-3 rounded-full cursor-pointer;
  }

  input[type='range']:focus::-webkit-slider-runnable-track {
    @apply h-gr-3 rounded-full cursor-pointer;
  }

  input[type='range']::-moz-range-track {
    @apply h-gr-3 rounded-full cursor-pointer;
  }

  input[type='range']::-ms-track {
    @apply h-gr-3 rounded-full cursor-pointer;
    background: transparent;
    border-color: transparent;
    color: transparent;
  }

  input[type='range']::-ms-fill-lower {
    @apply h-gr-3 rounded-full cursor-pointer;
  }

  input[type='range']:focus::-ms-fill-lower {
    @apply h-gr-3 rounded-full cursor-pointer;
  }

  input[type='range']::-ms-fill-upper {
    @apply h-gr-3 rounded-full;
  }

  input[type='range']:focus::-ms-fill-upper {
    @apply h-gr-3 rounded-full;
  }
</style>
